/**
 * Entry point for development and production PWA builds.
 */

import 'regenerator-runtime/runtime';

import App from './App.js';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { AZTheme, ToastHandler } from '@azmed-org/azcomponents';
import { AppUpdateWatcher } from './AZmed/AppUpdate/AppUpdateWatcher';
import '@radix-ui/themes/styles.css';
// Must come after Radix's stylesheet so our wrapper overrides win.
import './AZmed/radix-theme-overrides.css';

/**
 * EXTENSIONS
 * =================
 *
 * Importing and modifying the extensions our app uses HERE allows us to leverage
 * tree shaking and a few other niceties. However, by including them here they become
 * "baked in" to the published application.
 *
 * Depending on your use case/needs, you may want to consider not adding any extensions
 * by default HERE, and instead provide them via the extensions configuration key or
 * by using the exported `App` component, and passing in your extensions as props using
 * the defaultExtensions property.
 */
import OHIFVTKExtension from '@ohif/extension-vtk';
import OHIFDicomHtmlExtension from '@ohif/extension-dicom-html';
import OHIFDicomSegmentationExtension from '@ohif/extension-dicom-segmentation';
import OHIFDicomRtExtension from '@ohif/extension-dicom-rt';
import OHIFDicomMicroscopyExtension from '@ohif/extension-dicom-microscopy';
import OHIFDicomPDFExtension from '@ohif/extension-dicom-pdf';
import { ohifViewerConfig } from './AZmed/Config/OhifViewerConfig.js';
import { version } from '../package.json';

// Self-hosted web fonts (no Google Fonts call), before the app styles
import './AZmed/Assets/fonts/fonts.css';
import './AZmed/Index.css';

/*
 * Default Settings
 */
let config = ohifViewerConfig;

// Expose the app version on window
if (window) {
  window.version = version;
}

const appProps = {
  config,
  defaultExtensions: [
    OHIFVTKExtension,
    OHIFDicomHtmlExtension,
    OHIFDicomMicroscopyExtension,
    OHIFDicomPDFExtension,
    OHIFDicomSegmentationExtension,
    OHIFDicomRtExtension,
  ],
};

/** Create App */
const app = React.createElement(App, appProps, null);

/** Render */
const root = createRoot(document.getElementById('root'));
root.render(
  React.createElement(
    AZTheme,
    null,
    app,
    // Renders the toast portal used by `informUser` (feedback, ...).
    React.createElement(ToastHandler, null),
    // Watches for a newly deployed version, then reloads the app.
    React.createElement(AppUpdateWatcher, null)
  )
);
